import React, { useState, useEffect, useRef } from 'react';
import './AdminPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <h1>Admin Panel</h1>
        <p>Jose Ayala Portfolio</p>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
          />
          {error && <p className="admin-error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

function HeroTab({ data, onChange }) {
  return (
    <div className="admin-tab-content">
      <h3>Hero Section</h3>
      <label>Name</label>
      <input value={data.name || ''} onChange={e => onChange({ ...data, name: e.target.value })} />
      <label>Title</label>
      <input value={data.title || ''} onChange={e => onChange({ ...data, title: e.target.value })} />
      <label>Availability</label>
      <input value={data.availability || ''} onChange={e => onChange({ ...data, availability: e.target.value })} />
      <label>GitHub URL</label>
      <input value={data.github || ''} onChange={e => onChange({ ...data, github: e.target.value })} />
      <label>LinkedIn URL</label>
      <input value={data.linkedin || ''} onChange={e => onChange({ ...data, linkedin: e.target.value })} />
      <label>Typewriter Strings (one per line)</label>
      <textarea
        rows={6}
        value={(data.typewriter || []).join('\n')}
        onChange={e => onChange({ ...data, typewriter: e.target.value.split('\n') })}
      />
    </div>
  );
}

function SummaryTab({ data, onChange }) {
  return (
    <div className="admin-tab-content">
      <h3>Summary</h3>
      <label>About Summary</label>
      <textarea rows={8} value={data.summary || ''} onChange={e => onChange({ ...data, summary: e.target.value })} />
    </div>
  );
}

function AboutTab({ data, onChange }) {
  const lastCardRef = useRef(null);
  const updateCard = (index, field, value) => {
    const cards = [...data.cards];
    cards[index] = { ...cards[index], [field]: value };
    onChange({ ...data, cards });
  };

  const addCard = () => {
    const cards = [...data.cards, { title: 'New Card', icon: 'code', description: '' }];
    onChange({ ...data, cards });
    setTimeout(() => {
      if (lastCardRef.current) {
        lastCardRef.current.scrollIntoView({ behavior: 'smooth'});
      }
    }, 100);
  };

  const deleteCard = (index) => {
    if (!window.confirm('Delete this card? This cannot be undone.')) return;
    const cards = data.cards.filter((_, i) => i !== index);
    onChange({ ...data, cards });
  };

  return (
    <div className="admin-tab-content">
      <h3>About Section</h3>
      <div className="admin-tab-header" style={{ marginTop: '1.5rem' }}>
        <h4 style={{ margin: 0 }}>Cards</h4>
        <button className="admin-add-btn" onClick={addCard} type="button">+ Add Card</button>
      </div>
      {(data.cards || []).map((card, i) => (
        <div key={i} ref={i === data.cards.length - 1 ? lastCardRef : null} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{card.title || 'New Card'}</span>
            <button className="admin-delete-btn" onClick={() => deleteCard(i)} type="button">Delete</button>
          </div>
          <label>Title</label>
          <input value={card.title || ''} onChange={e => updateCard(i, 'title', e.target.value)} />
          <label>Icon (Font Awesome name)</label>
          <input value={card.icon || ''} onChange={e => updateCard(i, 'icon', e.target.value)} />
          <label>Description</label>
          <textarea rows={3} value={card.description || ''} onChange={e => updateCard(i, 'description', e.target.value)} />
        </div>
      ))}
    </div>
  );
}

function SkillsTab({ data, onChange }) {
  const updateSkill = (category, index, value) => {
    const updated = { ...data, [category]: [...data[category]] };
    updated[category][index] = value;
    onChange(updated);
  };

  const addSkill = (category) => {
    const updated = { ...data, [category]: [...data[category], ''] };
    onChange(updated);
  };

  const deleteSkill = (category, index) => {
    if (!window.confirm('Delete this skill?')) return;
    const updated = { ...data, [category]: data[category].filter((_, i) => i !== index) };
    onChange(updated);
  };

  return (
    <div className="admin-tab-content">
      <h3>Technical Skills</h3>
      {Object.entries(data).map(([category, skills]) => (
        <div key={category} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{category}</span>
            <button className="admin-add-btn" onClick={() => addSkill(category)} type="button">+ Add</button>
          </div>
          {skills.map((skill, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
              <input
                value={skill}
                onChange={e => updateSkill(category, i, e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="admin-delete-btn" onClick={() => deleteSkill(category, i)} type="button">Delete</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ProjectsTab({ data, onChange }) {
  const lastCardRef = useRef(null);
  const updateProject = (index, field, value) => {
    const projects = [...data];
    projects[index] = { ...projects[index], [field]: value };
    onChange(projects);
  };

  const addProject = () => {
    onChange([...data, {
      title: 'New Project',
      description: '',
      url: '',
      icon: 'code',
      status: 'active',
      gradient: 'linear-gradient(135deg, #0a1628 0%, #0d3b7a 100%)',
      technologies: [],
    }]);
    setTimeout(()=> {
      if (lastCardRef.current) {
        lastCardRef.current.scrollIntoView({behavior: 'smooth'});
      }
    }, 100);
  };

  const deleteProject = (index) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    onChange(data.filter((_, i) => i !== index));
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <h3>Projects</h3>
        <button className="admin-add-btn" onClick={addProject} type="button">+ Add Project</button>
      </div>
      {data.map((project, i) => (
        <div key={i} ref={i === data.length - 1 ? lastCardRef: null} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{project.title || 'New Project'}</span>
            <button className="admin-delete-btn" onClick={() => deleteProject(i)} type="button">Delete</button>
          </div>
          <label>Title</label>
          <input value={project.title || ''} onChange={e => updateProject(i, 'title', e.target.value)} />
          <label>Description</label>
          <textarea rows={3} value={project.description || ''} onChange={e => updateProject(i, 'description', e.target.value)} />
          <label>URL</label>
          <input value={project.url || ''} onChange={e => updateProject(i, 'url', e.target.value)} />
          <label>Status (active / complete)</label>
          <input value={project.status || ''} onChange={e => updateProject(i, 'status', e.target.value)} />
          <label>Icon (Font Awesome name)</label>
          <input value={project.icon || ''} onChange={e => updateProject(i, 'icon', e.target.value)} />
          <label>Technologies (comma separated)</label>
          <input
            placeholder="Python, React, Docker"
            value={(project.technologies || []).join(', ')}
            onChange={e => updateProject(i, 'technologies', e.target.value.split(',').map(t => t.trim()))}
          />
        </div>
      ))}
    </div>
  );
}


function BlogTab({ data, onChange }) {
  const lastCardRef = useRef(null);
  const updatePost = (index, field, value) => {
    const posts = [...data];
    posts[index] = { ...posts[index], [field]: value };
    onChange(posts);
  };

  const addPost = () => {
    onChange([...data, {
      id: Date.now(),
      title: 'New Incident Review',
      date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      tag: 'Engineering',
      summary: '',
      details: []
    }]);
    setTimeout(() => {
      if(lastCardRef.current){
        lastCardRef.current.scrollIntoView({behavior:'smooth'});
      }
    }, 100);
  };

  const deletePost = (index) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    onChange(data.filter((_, i) => i !== index));
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <h3>Blog Posts</h3>
        <button className="admin-add-btn" onClick={addPost} type="button">+ Add Post</button>
      </div>
      {data.map((post, i) => (
        <div key={i} ref={i === data.length - 1 ? lastCardRef:null} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{post.title || 'New Post'}</span>
            <button className="admin-delete-btn" onClick={() => deletePost(i)} type="button">Delete</button>
          </div>
          <label>Title</label>
          <input value={post.title || ''} onChange={e => updatePost(i, 'title', e.target.value)} />
          <label>Date</label>
          <input value={post.date || ''} onChange={e => updatePost(i, 'date', e.target.value)} />
          <label>Tag</label>
          <select value={post.tag || ''} onChange={e => updatePost(i, 'tag', e.target.value)}>
            <option value="">Select a tag</option>
            <option value="Incident Review">Incident Review</option>
            <option value="Lab Update">Lab Update</option>
            <option value="Tool Breakdown">Tool Breakdown</option>
            <option value="Project Update">Project Update</option>
          </select>
          <label>Summary</label>
          <textarea rows={3} value={post.summary || ''} onChange={e => updatePost(i, 'summary', e.target.value)} />
          <label>Details (one per line)</label>
          <textarea
            rows={6}
            value={(post.details || []).join('\n')}
            onChange={e => updatePost(i, 'details', e.target.value.split('\n').filter(Boolean))}
          />
        </div>
      ))}
    </div>
  );
}


function ExperienceTab({ data, onChange }) {
  const lastCardRef = useRef(null);

  const updateJob = (index, field, value) => {
    const jobs = [...data];
    jobs[index] = { ...jobs[index], [field]: value };
    onChange(jobs);
  };

  const addJob = () => {
    onChange([...data, { title: 'New Role', company: '', period: '', responsibilities: [] }]);
    setTimeout(() => {
      if (lastCardRef.current) {
        lastCardRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const deleteJob = (index) => {
    if (!window.confirm('Delete this role? This cannot be undone.')) return;
    onChange(data.filter((_, i) => i !== index));
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <h3>Experience</h3>
        <button className="admin-add-btn" onClick={addJob} type="button">+ Add Role</button>
      </div>
      {data.map((job, i) => (
        <div key={i} ref={i === data.length - 1 ? lastCardRef : null} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{job.title || 'New Role'}</span>
            <button className="admin-delete-btn" onClick={() => deleteJob(i)} type="button">Delete</button>
          </div>
          <label>Title</label>
          <input value={job.title || ''} onChange={e => updateJob(i, 'title', e.target.value)} />
          <label>Company</label>
          <input value={job.company || ''} onChange={e => updateJob(i, 'company', e.target.value)} />
          <label>Period</label>
          <input value={job.period || ''} onChange={e => updateJob(i, 'period', e.target.value)} />
          <label>Responsibilities (one per line)</label>
          <textarea
            rows={6}
            value={(job.responsibilities || []).join('\n')}
            onChange={e => updateJob(i, 'responsibilities', e.target.value.split('\n').filter(Boolean))}
          />
        </div>
      ))}
    </div>
  );
}

function EducationTab({ data, onChange }) {
  return (
    <div className="admin-tab-content">
      <h3>Education</h3>
      <div className="admin-card-group">
        <label>School</label>
        <input value={data.school || ''} onChange={e => onChange({ ...data, school: e.target.value })} />
        <label>Certificate</label>
        <input value={data.certificate || ''} onChange={e => onChange({ ...data, certificate: e.target.value })} />
        <label>Year</label>
        <input value={data.year || ''} onChange={e => onChange({ ...data, year: e.target.value })} />
        <label>Description</label>
        <textarea rows={3} value={data.description || ''} onChange={e => onChange({ ...data, description: e.target.value })} />
      </div>
    </div>
  );
}

function CertificationsTab({ data, onChange }) {
  const lastCardRef = useRef(null);
  const updateCert = (index, field, value) => {
    const certs = [...data];
    certs[index] = { ...certs[index], [field]: value };
    onChange(certs);
  };

  const addCert = () => {
    onChange([...data, { name: '', year: '', status: '', expected: '' }]);
    setTimeout(() => {
      if (lastCardRef.current) {
        lastCardRef.current.scrollIntoView({ behavior: 'smooth'});
      }
    }, 100);
  };

  const deleteCert = (index) => {
    if (!window.confirm('Delete this certification? This cannot be undone.')) return;
    onChange(data.filter((_, i) => i !== index));
  };

  return (
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <h3>Certifications</h3>
        <button className="admin-add-btn" onClick={addCert} type="button">+ Add Cert</button>
      </div>
      {data.map((cert, i) => (
        <div key={i} ref={i === data.length - 1 ? lastCardRef : null} className="admin-card-group">
          <div className="admin-card-group-header">
            <span>{cert.name || 'New Certification'}</span>
            <button className="admin-delete-btn" onClick={() => deleteCert(i)} type="button">Delete</button>
          </div>
          <label>Name</label>
          <input value={cert.name || ''} onChange={e => updateCert(i, 'name', e.target.value)} />
          <label>Year (if completed)</label>
          <input value={cert.year || ''} onChange={e => updateCert(i, 'year', e.target.value)} />
          <label>Status (if in progress)</label>
          <input value={cert.status || ''} onChange={e => updateCert(i, 'status', e.target.value)} />
          <label>Expected date (if in progress)</label>
          <input value={cert.expected || ''} onChange={e => updateCert(i, 'expected', e.target.value)} />
        </div>
      ))}
    </div>
  );
}


function VisitorsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/visitors`, {
      headers: { 'x-admin-token': token }
    })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Failed to load visitor data.'); setLoading(false); });
  }, [token]);

  if (loading) return <div className="admin-tab-content"><p>Loading...</p></div>;
  if (error) return <div className="admin-tab-content"><p>{error}</p></div>;

  const chart = data.dailyChart || [];
  const maxVal = Math.max(...chart.map(d => d.visits), 1);
  const chartH = 80;
  const barW = 14;
  const gap = 4;
  const chartW = chart.length * (barW + gap);

  return (
    <div className="admin-tab-content">
      <h3>Visitor Analytics</h3>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ flex: 1, padding: '1rem', background: 'var(--bg-secondary, #1a1a2e)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.uniqueVisitors}</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Unique Visitors</div>
        </div>
        <div style={{ flex: 1, padding: '1rem', background: 'var(--bg-secondary, #1a1a2e)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.totalVisits}</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Total Visits</div>
        </div>
        <div style={{ flex: 1, padding: '1rem', background: 'var(--bg-secondary, #1a1a2e)', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{data.todayVisits}</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Today</div>
        </div>
      </div>

      <div className="admin-card-group" style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ marginBottom: '1rem' }}>Last 14 Days</h4>
        <svg width={chartW} height={chartH + 24} style={{ overflow: 'visible' }}>
          {chart.map((d, i) => {
            const barH = d.visits === 0 ? 2 : Math.max(4, (d.visits / maxVal) * chartH);
            const x = i * (barW + gap);
            const y = chartH - barH;
            const isToday = d.date === new Date().toISOString().slice(0, 10);
            return (
              <g key={i}>
                <rect
                  x={x} y={y} width={barW} height={barH}
                  rx={2}
                  fill={isToday ? '#4a9eff' : '#2a4a7f'}
                  opacity={d.visits === 0 ? 0.3 : 1}
                />
                {d.visits > 0 && (
                  <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="#aaa">
                    {d.visits}
                  </text>
                )}
                {(i === 0 || i === 6 || i === 13) && (
                  <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize="9" fill="#666">
                    {d.date.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <h4 style={{ marginBottom: '0.75rem' }}>All Visitors</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #333' }}>
            <th style={{ textAlign: 'left', padding: '0.5rem' }}>Visitor Hash</th>
            <th style={{ textAlign: 'center', padding: '0.5rem' }}>Visits</th>
            <th style={{ textAlign: 'right', padding: '0.5rem' }}>Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {data.visitors.map((v, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #222' }}>
              <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.hash}</td>
              <td style={{ padding: '0.5rem', textAlign: 'center' }}>{v.visits}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', opacity: 0.7, fontSize: '0.8rem' }}>
                {v.lastSeen ? new Date(v.lastSeen).toLocaleString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ResumeTab({ token }) {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/resume/status`, {
      headers: { 'x-admin-token': token }
    })
      .then(res => res.json())
      .then(d => setStatus(d))
      .catch(() => setStatus({ exists: false }));
  }, [token]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMessage(null);
    const formData = new FormData();
    formData.append('resume', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/resume`, {
        method: 'POST',
        headers: { 'x-admin-token': token },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage({ type: 'success', text: 'Resume uploaded successfully.' });
      setStatus({ exists: true, lastModified: new Date().toISOString(), size: file.size });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setUploading(false);
  };

  return (
    <div className="admin-tab-content">
      <h3>Resume</h3>
      <div className="admin-card-group">
        <h4>Current Resume</h4>
        {status === null && <p>Loading...</p>}
        {status && !status.exists && <p style={{ opacity: 0.6 }}>No resume uploaded yet.</p>}
        {status && status.exists && (
          <div style={{ marginBottom: '1rem' }}>
            <p>✅ resume.pdf</p>
            <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>
              Size: {(status.size / 1024).toFixed(1)} KB · 
              Last updated: {new Date(status.lastModified).toLocaleString()}
            </p>
            
            <a
              href={`${BACKEND_URL}/api/resume/download`}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '0.85rem', color: 'var(--accent)' }}
            >
              Preview current resume
            </a>
          </div>
        )}
        <h4 style={{ marginTop: '1rem' }}>Upload New Resume</h4>
        <p style={{ fontSize: '0.85rem', opacity: 0.6, marginBottom: '0.75rem' }}>
          PDF only, max 5MB. Replaces the current file.
        </p>
        <input
          type="file"
          accept="application/pdf"
          onChange={handleUpload}
          disabled={uploading}
          style={{ marginBottom: '0.5rem' }}
        />
        {uploading && <p style={{ opacity: 0.6 }}>Uploading...</p>}
        {message && (
          <p style={{ color: message.type === 'success' ? '#34c759' : '#ff3b30' }}>
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [token, setToken] = useState(null);
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/content`)
      .then(res => res.json())
      .then(data => setContent(data));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);

   // Clean empty strings from technology arrays
    const cleaned = {
      ...content,
      projects: content.projects.map(p => ({
        ...p,
        technologies: (p.technologies || []).filter(t => t.trim() !== '')
      }))
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleaned),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus('error');
    }
    setSaving(false);
    setTimeout(() => setSaveStatus(null), 3000);
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  if (!content) return <div className="admin-loading">Loading content...</div>;

  const TABS = ['hero', 'summary', 'about','skills', 'projects', 'blog', 'experience', 'education', 'certifications', 'visitors', 'resume'];

  return (
    <div className="admin-panel">
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>Admin Panel</h2>
          <p>josedevelops.dev</p>
        </div>
        <nav>
          {TABS.map(tab => (
            <button
              key={tab}
              className={"admin-nav-btn" + (activeTab === tab ? " active" : "")}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <button
            className={"admin-save-btn" + (saving ? " saving" : "")}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saveStatus === 'saved' && <p className="admin-save-success">Saved successfully</p>}
          {saveStatus === 'error' && <p className="admin-save-error">Save failed</p>}
          <button className="admin-logout-btn" onClick={() => setToken(null)}>Logout</button>
        </div>
      </div>
      <div className="admin-main">
        {activeTab === 'hero' && (
          <HeroTab data={content.hero} onChange={val => setContent({ ...content, hero: val })} />
        )}
        {activeTab === 'summary' && (
          <SummaryTab data={content.about} onChange={val => setContent({ ...content, about: val })} />
        )}
        {activeTab === 'skills' && (
          <SkillsTab data={content.skills} onChange={val => setContent({ ...content, skills: val })} />
        )}
        {activeTab === 'about' && (
          <AboutTab data={content.about} onChange={val => setContent({ ...content, about: val })} />
        )}
        {activeTab === 'projects' && (
          <ProjectsTab data={content.projects} onChange={val => setContent({ ...content, projects: val })} />
        )}
        {activeTab === 'blog' && (
          <BlogTab data={content.blog} onChange={val => setContent({ ...content, blog: val })} />
        )}
        {activeTab === 'experience' && (
          <ExperienceTab data={content.experience} onChange={val => setContent({ ...content, experience: val })} />
        )}
        {activeTab === 'education' && (
          <EducationTab data={content.education} onChange={val => setContent({ ...content, education: val })} />
        )}
        {activeTab === 'certifications' && (
          <CertificationsTab data={content.certifications} onChange={val => setContent({ ...content, certifications: val })} />
        )}
        {activeTab === 'visitors' && (
          <VisitorsTab token={token} />
        )}
        {activeTab === 'resume' && (
          <ResumeTab token={token} />
        )}
      </div>
    </div>
  );
}
